import { useEffect, useState } from 'react';
import type { SiteBanner } from '../../shared/types';
import { api } from '../lib/api';
import { Icon } from './Icon';

const KEY = 'rabd.dismissedBanners';
const keyOf = (b: SiteBanner) => `${b.id}:${b.updatedAt}`;
const readDismissed = (): string[] => { try { return JSON.parse(localStorage.getItem(KEY) ?? '[]'); } catch { return []; } };

/**
 * Management notices (Alerts tool) in a thin bar at the top of every page. Members can dismiss
 * one; it comes back only if management edits it. New notices show within a few minutes.
 */
export function BannerBar({ initial }: { initial: SiteBanner[] }) {
  const [banners, setBanners] = useState(initial);
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);
  useEffect(() => setBanners(initial), [initial]);
  useEffect(() => {
    const t = setInterval(() => { api.get<{ banners: SiteBanner[] }>('/api/me/banners').then((r) => setBanners(r.banners)).catch(() => {}); }, 4 * 60_000);
    return () => clearInterval(t);
  }, []);
  const shown = banners.filter((b) => !dismissed.includes(keyOf(b)));
  if (!shown.length) return null;
  const dismiss = (b: SiteBanner) => {
    // Keep only keys of banners that still exist, so the list doesn't grow forever.
    const next = [...dismissed.filter((k) => banners.some((x) => keyOf(x) === k)), keyOf(b)];
    setDismissed(next);
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* private mode */ }
    void api.post(`/api/me/banners/${encodeURIComponent(b.id)}/dismiss`, {}).catch(() => {});
  };
  return (
    <div className="banner-stack" role="region" aria-label="Announcements">
      {shown.map((b) => (
        <div key={b.id} className={`site-banner tone-${b.tone}`} role={b.tone === 'danger' ? 'alert' : 'status'}>
          <Icon name={b.tone === 'ok' ? 'check' : b.tone === 'info' ? 'megaphone' : 'warn'} size={15} />
          <span className="site-banner-text">{b.message}</span>
          {b.link && <a className="site-banner-link" href={b.link.url} target={b.link.url.startsWith('/') ? undefined : '_blank'} rel="noreferrer noopener">{b.link.label || 'More'}</a>}
          <button className="site-banner-x" onClick={() => dismiss(b)} aria-label="Dismiss this notice"><Icon name="close" size={14} /></button>
        </div>
      ))}
    </div>
  );
}
